import React, { useState } from 'react';
import axios from 'axios';
import { FiX, FiSearch, FiPlus } from 'react-icons/fi';

const CreateGodGroupForm = ({ onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        groupName: '',
        handler: null,
        participants: [],
        renewalDate: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [suggestedName, setSuggestedName] = useState('');
    const [members, setMembers] = useState([]);
    
    // Separate search states for handler and participants
    const [handlerSearchTerm, setHandlerSearchTerm] = useState('');
    const [participantSearchTerm, setParticipantSearchTerm] = useState('');
    const [showHandlerSearch, setShowHandlerSearch] = useState(false);
    const [showParticipantSearch, setShowParticipantSearch] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [handlerSearchResults, setHandlerSearchResults] = useState([]);
    const [participantSearchResults, setParticipantSearchResults] = useState([]);
    const [handlerSearchLoading, setHandlerSearchLoading] = useState(false);
    const [participantSearchLoading, setParticipantSearchLoading] = useState(false);

    // Modified to search members using API
    const searchMembers = async (searchTerm) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No auth token found');
                setError('Authentication required');
                return [];
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godmembers/search?term=${encodeURIComponent(searchTerm)}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );

            if (response.data.success) {
                return response.data.data;
            }
            return [];
        } catch (err) {
            console.error('Search error:', err);
            return [];
        }
    };

    const runHandlerSearch = async () => {
        if (!handlerSearchTerm.trim()) {
            setHandlerSearchResults([]);
            return;
        }
        setShowHandlerSearch(true);
        setHandlerSearchLoading(true);
        try {
            const results = await searchMembers(handlerSearchTerm.trim());
            setHandlerSearchResults(results);
        } finally {
            setHandlerSearchLoading(false);
        }
    };

    const runParticipantSearch = async () => {
        if (!participantSearchTerm.trim()) {
            setParticipantSearchResults([]);
            return;
        }
        setShowParticipantSearch(true);
        setParticipantSearchLoading(true);
        try {
            const results = await searchMembers(participantSearchTerm.trim());
            setParticipantSearchResults(results);
        } finally {
            setParticipantSearchLoading(false);
        }
    };

    const handleSelectHandler = (member) => {
        console.log('Selected handler:', member);
        setFormData(prev => ({
            ...prev,
            handler: member
        }));
        setHandlerSearchTerm('');
        setShowHandlerSearch(false);
    };

    const handleSelectParticipant = (member) => {
        const newParticipant = {
            joinDate: new Date().toISOString().split('T')[0],
            memberID: member._id,
            memberInfo: member,
            removeDate: null,
            remarks: ''
        };

        setFormData(prev => ({
            ...prev,
            participants: [...prev.participants, newParticipant]
        }));
        setParticipantSearchTerm('');
        setShowParticipantSearch(false);
    };

    const handleRemoveParticipant = (index) => {
        setFormData(prev => ({
            ...prev,
            participants: prev.participants.filter((_, i) => i !== index)
        }));
    };

    const handleParticipantDateChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            participants: prev.participants.map((p, i) => 
                i === index 
                    ? { ...p, [field]: value }
                    : p
            )
        }));
    };

    const handleParticipantRemarksChange = (index, value) => {
        setFormData(prev => ({
            ...prev,
            participants: prev.participants.map((p, i) => 
                i === index 
                    ? { ...p, remarks: value }
                    : p
            )
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuggestedName('');

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/godgroups`,
                {
                    groupName: formData.groupName,
                    groupMembers: {
                        handler: formData.handler._id,
                        participants: formData.participants.map(p => ({
                            memberID: p.memberID,
                            joinDate: p.joinDate,
                            removeDate: p.removeDate || null,
                            remarks: p.remarks
                        }))
                    },
                    renewalDate: formData.renewalDate
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                onSuccess && onSuccess(response.data.data);
            }
        } catch (err) {
            if (err.response?.data?.suggestedName) {
                setError(`Group name "${formData.groupName}" already exists`);
                setSuggestedName(err.response.data.suggestedName);
            } else {
                setError(err.response?.data?.error || 'Failed to create god group');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUseSuggestedName = () => {
        setFormData(prev => ({
            ...prev,
            groupName: suggestedName
        }));
        setSuggestedName('');
        setError('');
    };

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Create New God Group</h3>
                <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                    <FiX />
                </button>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                    <p>{error}</p>
                    {suggestedName && (
                        <div className="mt-2">
                            <p className="font-medium">Suggested name: {suggestedName}</p>
                            <button
                                onClick={handleUseSuggestedName}
                                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Use this name
                            </button>
                        </div>
                    )}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Group Name</label>
                    <input
                        type="text"
                        name="groupName"
                        value={formData.groupName}
                        onChange={(e) => {
                            setFormData(prev => ({
                                ...prev,
                                groupName: e.target.value
                            }));
                            // Clear suggestion when user types
                            if (suggestedName) {
                                setSuggestedName('');
                            }
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Handler</label>
                    {formData.handler ? (
                        <div className="mt-1 flex items-center justify-between p-2 border rounded-md">
                            <span>{formData.handler.fullName}</span>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, handler: null }))}
                                className="text-red-600 hover:text-red-800"
                            >
                                <FiX />
                            </button>
                        </div>
                    ) : (
                        <div className="mt-1">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="GodID or name, then Enter or click Search"
                                    value={handlerSearchTerm}
                                    onChange={(e) => {
                                        setHandlerSearchTerm(e.target.value);
                                        setHandlerSearchResults([]);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            runHandlerSearch();
                                        }
                                    }}
                                    onFocus={() => setShowHandlerSearch(true)}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                                />
                                <button
                                    type="button"
                                    onClick={runHandlerSearch}
                                    className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
                                    aria-label="Search handler"
                                >
                                    <FiSearch />
                                </button>
                            </div>
                            {showHandlerSearch && handlerSearchTerm.trim() !== '' && (
                                <div className="mt-2 border rounded-md max-h-40 overflow-y-auto">
                                    {handlerSearchLoading ? (
                                        <div className="p-2 text-gray-500">Loading...</div>
                                    ) : handlerSearchResults.length > 0 ? (
                                        handlerSearchResults.map(member => (
                                            <div
                                                key={member._id}
                                                className="p-2 hover:bg-gray-100 cursor-pointer"
                                                onClick={() => handleSelectHandler(member)}
                                            >
                                                {member.fullName}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-2 text-gray-500">No matches found</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Participants</label>
                    <div className="mt-1">
                        <div className="flex gap-2 mb-2">
                            <div className="relative flex-1 flex gap-1">
                                <input
                                    type="text"
                                    placeholder="GodID or name, then Enter or click Search"
                                    value={participantSearchTerm}
                                    onChange={(e) => {
                                        setParticipantSearchTerm(e.target.value);
                                        setParticipantSearchResults([]);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            runParticipantSearch();
                                        }
                                    }}
                                    onFocus={() => setShowParticipantSearch(true)}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                                />
                                <button
                                    type="button"
                                    onClick={runParticipantSearch}
                                    className="p-2 bg-gray-100 rounded-md hover:bg-gray-200 shrink-0"
                                    aria-label="Search participant"
                                >
                                    <FiSearch />
                                </button>
                                {showParticipantSearch && participantSearchTerm.trim() !== '' && (
                                    <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-white border rounded-md max-h-40 overflow-y-auto shadow-lg">
                                        {participantSearchLoading ? (
                                            <div className="p-2 text-gray-500">Loading...</div>
                                        ) : participantSearchResults.length > 0 ? (
                                            participantSearchResults.map(member => (
                                                <div
                                                    key={member._id}
                                                    className="p-2 hover:bg-gray-100 cursor-pointer"
                                                    onClick={() => handleSelectParticipant(member)}
                                                >
                                                    {member.fullName}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-2 text-gray-500">No matches found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelectedParticipant(null)}
                                className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                            >
                                <FiPlus />
                            </button>
                        </div>
                        <div className="space-y-2">
                            {formData.participants.map((participant, index) => (
                                <div key={index} className="space-y-2 p-4 border rounded-md">
                                    <div className="flex items-center justify-between">
                                        <div className="font-medium">
                                            {participant.memberInfo?.fullName || 'Unknown Member'}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveParticipant(index)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            <FiX />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Join Date</label>
                                            <input
                                                type="date"
                                                value={participant.joinDate}
                                                onChange={(e) => handleParticipantDateChange(index, 'joinDate', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Remove Date</label>
                                            <input
                                                type="date"
                                                value={participant.removeDate || ''}
                                                onChange={(e) => handleParticipantDateChange(index, 'removeDate', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Remarks</label>
                                        <textarea
                                            value={participant.remarks}
                                            onChange={(e) => handleParticipantRemarksChange(index, e.target.value)}
                                            rows="2"
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                                            placeholder="Add any remarks here..."
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Renewal Date</label>
                    <input
                        type="date"
                        name="renewalDate"
                        value={formData.renewalDate}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            renewalDate: e.target.value
                        }))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                    />
                </div>

                <div className="flex justify-end space-x-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading || !formData.handler || formData.participants.length === 0}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                    >
                        {loading ? 'Creating...' : 'Create Group'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateGodGroupForm; 